import './CustomRedactionTextAccordion.scss';
import React, {useEffect, useRef} from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {ReactComponent as DeleteIcon} from '../../../../../assets/images/delete.svg';
import ArrButton from '../../../../common/arr-button/ArrButton';
import TagsSection from '../tags-section/TagsSection';
import TextPropertyToolbar from '../text-property-toolbar/TextPropertyToolbar';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {
  ExtendedTreeItemProps,
  TooltipData,
} from '../../../../../shared/interfaces';
import {TagItem} from '../../../../../api/types/production.type';
import {ISelectItem} from '../../../../../shared/types';
import {t} from 'i18next';

export interface RedactionText {
  selectedTags: TagItem[];
  disabledTags?: TagItem[];
  placeholderText: string;
  abbreviatedText?: string;
}

interface CustomRedactionTextAccordionProps {
  id: string;
  index: number;
  redactionTextData: RedactionText;
  onDelete: (id: string) => void;
  onRedactionTextDataChange: (id: string, data: RedactionText) => void;
  tagsTreeData?: TreeViewBaseItem<ExtendedTreeItemProps>[];
  insertFieldTypes: ISelectItem[];
  metadataFieldOptions: ISelectItem[];
  productionFieldOptions: ISelectItem[];
  disabled?: boolean;
  showAbbreviatedText?: boolean;
  contentColumnCount?: 2 | 3;
  tagsFlyoutTooltip?: TooltipData;
}

const CustomRedactionTextAccordion: React.FC<
  CustomRedactionTextAccordionProps
> = ({
  id,
  index,
  redactionTextData,
  onDelete,
  onRedactionTextDataChange,
  tagsTreeData = [],
  insertFieldTypes,
  metadataFieldOptions,
  productionFieldOptions,
  disabled = false,
  showAbbreviatedText = true,
  contentColumnCount = 3,
  tagsFlyoutTooltip,
}) => {
  const redactionTextDetailsRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!disabled) {
      return;
    }

    let animationFrameId = 0;

    const getControlText = (element: HTMLElement): string =>
      (element.getAttribute('aria-label') || element.textContent || '')
        .trim()
        .replace(/\s+/g, ' ');

    const setNonFocusable = (element: HTMLElement | null | undefined) => {
      if (!element) {
        return;
      }
      element.setAttribute('tabindex', '-1');
      element.setAttribute('aria-disabled', 'true');
    };

    const setFocusable = (
      element: HTMLElement | null | undefined,
      ariaLabel?: string
    ) => {
      if (!element) {
        return;
      }
      element.setAttribute('tabindex', '0');
      element.setAttribute('aria-disabled', 'true');
      if (ariaLabel) {
        element.setAttribute('aria-label', ariaLabel);
      }
    };

    const updateReadOnlyFocusOrder = () => {
      const detailsElement = redactionTextDetailsRef.current;
      if (!detailsElement) return;

      detailsElement
        .querySelectorAll<HTMLElement>(
          '[tabindex], button, input, select, textarea, a[href], [role="button"], [role="combobox"], [role="listbox"], [role="option"]'
        )
        .forEach(setNonFocusable);

      detailsElement
        .querySelectorAll<HTMLElement>(
          '[data-testid^="filtered-selected-tags-chip-"]'
        )
        .forEach((chipElement) => {
          const chipText = getControlText(chipElement);
          if (chipText) {
            chipElement
              .querySelectorAll<HTMLElement>(
                'button, [role="button"], [tabindex]'
              )
              .forEach((nestedElement) => {
                if (nestedElement !== chipElement) {
                  setNonFocusable(nestedElement);
                }
              });
            setFocusable(chipElement, chipText);
          }
        });
    };

    const scheduleFocusUpdate = () => {
      window.cancelAnimationFrame(animationFrameId);
      animationFrameId = window.requestAnimationFrame(updateReadOnlyFocusOrder);
    };

    scheduleFocusUpdate();

    const observer = new MutationObserver(scheduleFocusUpdate);
    if (redactionTextDetailsRef.current) {
      observer.observe(redactionTextDetailsRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['tabindex', 'disabled', 'aria-disabled', 'value'],
      });
    }

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      observer.disconnect();
    };
  }, [
    disabled,
    redactionTextData.selectedTags,
    redactionTextData.placeholderText,
    redactionTextData.abbreviatedText,
  ]);
  useEffect(() => {
    const detailsElement = redactionTextDetailsRef.current;

    if (!detailsElement || disabled) return;

    const getDisplayName = (item: unknown): string => {
      const record = item as Record<string, unknown>;
      return String(
        record.tagLabel ?? record.name ?? record.text ?? record.value ?? ''
      );
    };

    const selectedTagLabels = redactionTextData.selectedTags
      .map(getDisplayName)
      .filter(Boolean);

    const removableControls = Array.from(
      detailsElement.querySelectorAll<HTMLElement>(
        'button, [role="button"], [aria-label*="Remove"]'
      )
    );

    selectedTagLabels.forEach((label) => {
      const matchingControl = removableControls.find((control) =>
        control.textContent?.includes(label)
      );

      matchingControl?.setAttribute('aria-label', `Remove ${label}`);
    });
  }, [disabled, redactionTextData.selectedTags]);

  const handleTagsChange = (tags: TagItem[]) => {
    if (disabled) return;
    onRedactionTextDataChange(id, {
      ...redactionTextData,
      selectedTags: tags,
    });
  };

  const handleTextChange = (text: string) => {
    if (disabled) return;
    onRedactionTextDataChange(id, {
      ...redactionTextData,
      placeholderText: text,
    });
  };

  const handleAbbreviatedTextChange = (text: string) => {
    if (disabled) return;
    onRedactionTextDataChange(id, {
      ...redactionTextData,
      abbreviatedText: text,
    });
  };

  return (
    <Box className="custom-redaction-text-accordion-wrapper">
      <Box className="custom-redaction-text-header-wrapper">
        <Accordion className="custom-redaction-text-accordion" defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className="custom-redaction-text-accordion-summary"
            aria-label={`Redaction ${index + 1} collapse toggle`}
          >
            <Typography
              className="typo-text subhead4_size custom-redaction-text-title"
              data-testid={`custom-redaction-text-accordion-title`}
            >
              {t(
                'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.redactionText.redactionItem.title',
                {number: index + 1}
              )}
            </Typography>
          </AccordionSummary>

          <AccordionDetails
            className="custom-redaction-text-details"
            ref={redactionTextDetailsRef}
          >
            <Box
              className={`custom-redaction-text-content columns-${contentColumnCount}`}
            >
              {/* Tags Section */}
              <TagsSection
                headerTitle={t(
                  'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.redactionTags.label'
                )}
                buttonTitle={t(
                  'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.redactionTags.buttonText'
                )}
                selectedTags={redactionTextData.selectedTags}
                onTagsChange={handleTagsChange}
                tagsTreeData={tagsTreeData}
                disabledTags={redactionTextData.disabledTags}
                disabled={disabled}
                flyoutTooltip={tagsFlyoutTooltip}
              />

              {/* Text Property Toolbar */}
              <TextPropertyToolbar
                variant="with-title"
                dataTestId="with-title"
                title={t(
                  'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.redactionText.redactionItem.placeholderText.title'
                )}
                toolbarText={redactionTextData.placeholderText}
                insertFieldTypes={insertFieldTypes}
                metadataFieldOptions={metadataFieldOptions}
                productionFieldOptions={productionFieldOptions}
                onToolbarTextChange={handleTextChange}
                onInsertField={(field) => {
                  handleTextChange(
                    `${redactionTextData.placeholderText}$${field}$`
                  );
                }}
                placeholder={t(
                  'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.redactionText.redactionItem.placeholderText.placeholder'
                )}
                disabled={disabled}
              />

              {showAbbreviatedText && (
                <TextPropertyToolbar
                  dataTestId="with-only-title"
                  variant="with-only-title"
                  title={t(
                    'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.redactionText.redactionItem.abbreviatedText.title'
                  )}
                  toolbarText={redactionTextData.abbreviatedText ?? ''}
                  onToolbarTextChange={handleAbbreviatedTextChange}
                  placeholder={t(
                    'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.redactionText.redactionItem.abbreviatedText.placeholder'
                  )}
                  disabled={disabled}
                />
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
        {!disabled && (
          <ArrButton
            variant="secondary"
            size="small"
            onClick={() => onDelete(id)}
            className="delete-btn"
            dataTestId="custom-redaction-text-accordion-button"
          >
            <DeleteIcon />
          </ArrButton>
        )}
      </Box>
    </Box>
  );
};

export default CustomRedactionTextAccordion;
